import { motion } from "framer-motion";
import { BedDouble, Waves, Mountain, Palette } from "lucide-react";

const VILLAS = [
    {
        name: "Casa Hilda",
        tagline: "Three-bedroom villa",
        image:
            "https://static.wixstatic.com/media/2ffe17_3e57a6d38e9744c789a2104fc7fe249b~mv2.jpeg",
        copy:
            "Unwind in the outdoor jacuzzi while taking in the hill country at sunset. A romantic Juliet balcony, hand-painted ceilings and floor-to-wall mosaics by our in-house artist make Casa Hilda a quiet, luminous retreat.",
        features: [
            { icon: BedDouble, label: "3 bedrooms" },
            { icon: Waves, label: "Outdoor jacuzzi" },
            { icon: Mountain, label: "Juliet balcony" },
            { icon: Palette, label: "Hand-painted ceilings" },
        ],
    },
    {
        name: "Casa Enrique",
        tagline: "Two-bedroom hacienda",
        image:
            "https://static.wixstatic.com/media/2ffe17_9d19afbdfb0943ec93d87608e4eb1b63~mv2.jpeg",
        copy:
            "A hacienda-style villa woven into the heart of the estate — every corner alive with vibrant mosaics in the spirit of Van Gogh, opening onto the Michelangelo-inspired granite courtyard. A sanctuary where art, love and quiet vistas intertwine.",
        features: [
            { icon: BedDouble, label: "2 bedrooms" },
            { icon: Palette, label: "Van Gogh mosaics" },
            { icon: Mountain, label: "Courtyard views" },
            { icon: Waves, label: "Estate access" },
        ],
    },
];

export default function Stay() {
    return (
        <section
            id="stay"
            className="section-py container-px bg-[hsl(var(--muted))]"
            data-testid="stay-section"
        >
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-end mb-16">
                <div className="lg:col-span-7">
                    <p className="overline">Guest Houses</p>
                    <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
                        Stay the <em className="italic text-[hsl(var(--primary))]">whole weekend.</em>
                    </h2>
                </div>
                <p className="lg:col-span-5 text-muted-foreground text-lg font-light leading-relaxed">
                    Two villas on the estate — perfect for the wedding party, a quiet
                    weekend escape, or extending the celebration with your closest guests.
                    <span className="block mt-2 italic font-serif text-sm text-foreground/70">
                        &mdash; &ldquo;The sight of the stars makes me dream.&rdquo; — Vincent van Gogh
                    </span>
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
                {VILLAS.map((v, i) => (
                    <motion.article
                        key={v.name}
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: i * 0.1 }}
                        viewport={{ once: true, margin: "-60px" }}
                        className="bg-background border border-border flex flex-col"
                        data-testid={`villa-${v.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                        <div className="relative overflow-hidden">
                            <img
                                src={v.image}
                                alt={v.name}
                                className="w-full h-[320px] sm:h-[420px] object-cover transition-transform duration-[1500ms] hover:scale-[1.04]"
                            />
                            <span className="absolute top-6 left-6 font-serif italic text-white text-lg tracking-wide drop-shadow">
                                0{i + 1}
                            </span>
                        </div>

                        <div className="p-8 sm:p-10 flex flex-col gap-6 flex-1">
                            <div>
                                <p className="overline text-[10px]">{v.tagline}</p>
                                <h3 className="mt-2 font-serif text-3xl tracking-tight">{v.name}</h3>
                            </div>
                            <p className="text-muted-foreground text-base font-light leading-relaxed">
                                {v.copy}
                            </p>

                            <div className="grid grid-cols-2 gap-x-6 gap-y-4 pt-2 border-t border-border">
                                {v.features.map((f) => (
                                    <div
                                        key={f.label}
                                        className="flex items-center gap-3 text-sm font-light"
                                    >
                                        <f.icon
                                            className="h-4 w-4 text-[hsl(var(--primary))]"
                                            strokeWidth={1.3}
                                        />
                                        {f.label}
                                    </div>
                                ))}
                            </div>

                            <a
                                href="#booking"
                                className="mt-4 inline-block text-center px-6 py-3 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-foreground))] transition-all duration-300 text-xs tracking-[0.25em] uppercase"
                                data-testid={`inquire-${v.name.toLowerCase().replace(/\s+/g, "-")}-btn`}
                            >
                                Inquire about {v.name}
                            </a>
                        </div>
                    </motion.article>
                ))}
            </div>

            <p className="mt-12 text-xs uppercase tracking-[0.25em] text-muted-foreground text-center">
                For nightly rates and availability, please reach out via the form below.
            </p>
        </section>
    );
}
